/// <amd-module name="Wit/WorkItemForm/Header/WorkItemNotificationSettingsDialog" />
import { IFollowsService } from "Notifications/Common/Contracts/FollowsService";
import { KnownArtifactTypes } from "Notifications/Common/Services/FollowsService";
import * as React from "react";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "VSSUI/Dialog";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IWorkItemFormPageContext, WorkItemFormPageContext } from "../Common/Hooks/";

export enum FollowType {
    All = "All",
    Custom = "Custom",
    None = "None"
}

export interface IWorkItemNotificationSettingsDialogProps {
    onDismiss: (following?: boolean) => void;
}

export interface IWorkItemNotificationSettingsDialogState {
    loading: boolean;
    selectedSubscriptionType: FollowType;
}

export class WorkItemNotificationSettingsDialog extends React.Component<
    IWorkItemNotificationSettingsDialogProps,
    IWorkItemNotificationSettingsDialogState
> {
    public static contextType = WorkItemFormPageContext;

    public context: IWorkItemFormPageContext;

    private workItemId: string;
    private followsService: IFollowsService;
    private originalFollowType: FollowType;
    private originalCustomOptions: Set<string>;
    private customOptions: Set<string>;

    public constructor(props: IWorkItemNotificationSettingsDialogProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this.followsService = context.pageContext.getService<IFollowsService>("IFollowsService");

        this.state = {
            loading: true,
            selectedSubscriptionType: FollowType.None
        };

        this.customOptions = new Set();

        this.workItemId = context.workItem.getUniqueId().toString();
    }

    public async componentDidMount() {
        const subscription = await this.followsService.getSubscription(KnownArtifactTypes.WorkItem, this.workItemId);
        const notificationState = { loading: false, selectedSubscriptionType: FollowType.None };

        if (subscription != null) {
            if (subscription.description.indexOf("fieldFilters") != -1) {
                notificationState.selectedSubscriptionType = FollowType.Custom;
                JSON.parse(subscription.description).fieldFilters.forEach((field: string) => {
                    this.customOptions.add(field);
                });
            } else {
                notificationState.selectedSubscriptionType = FollowType.All;
            }
        }

        this.originalFollowType = notificationState.selectedSubscriptionType;
        this.originalCustomOptions = new Set(this.customOptions);
        this.setState(notificationState);
    }

    render() {
        return (
            <Dialog
                footerButtonProps={[
                    { text: Resources.Save, primary: true, onClick: this._handleSave, disabled: this._isSaveDisabled() },
                    { text: Resources.Cancel, onClick: () => this.props.onDismiss() }
                ]}
                onDismiss={this.props.onDismiss}
                overlay={this.state.loading ? {} : undefined}
                titleProps={{ text: Resources.NotificationSettings }}
            >
                <RadioButtonGroup onSelect={this._handleSelect} selectedButtonId={this.state.selectedSubscriptionType}>
                    <div className="flex-column flex-grow rhythm-vertical-8">
                        <RadioButton
                            ariaDescribedBy="notification-not-subscribed-description"
                            className="font-weight-semibold"
                            id="None"
                            text={Resources.NotSubscribed}
                        />
                        <div className="notification-options-radio-description" id="notification-not-subscribed-description">
                            {Resources.NotSubscribedDescription}
                        </div>
                        <RadioButton
                            ariaDescribedBy="notification-subscribed-description"
                            className="font-weight-semibold"
                            id="All"
                            text={Resources.Subscribed}
                        />
                        <div className="notification-options-radio-description" id="notification-subscribed-description">
                            {Resources.SubscribedDescription}
                        </div>
                        <RadioButton
                            ariaDescribedBy="notification-custom-description"
                            className="font-weight-semibold"
                            id="Custom"
                            text={Resources.Custom}
                        />
                        <div className="notification-options-radio-description" id="notification-custom-description">
                            {Resources.CustomNotificationDescription}
                        </div>
                        <div className="rhythm-vertical-8 flex-column custom-checkbox">
                            <Checkbox
                                checked={this.customOptions.has(CoreFieldRefNames.State)}
                                className="notification-options-custom-checkbox"
                                disabled={this.state.selectedSubscriptionType != FollowType.Custom}
                                label={Resources.StateChanged}
                                onChange={(event, checked) => {
                                    this._handleCheckbox(CoreFieldRefNames.State, checked);
                                }}
                            />
                            <Checkbox
                                checked={this.customOptions.has(CoreFieldRefNames.AssignedTo)}
                                className="notification-options-custom-checkbox"
                                disabled={this.state.selectedSubscriptionType != FollowType.Custom}
                                label={Resources.AssignedToChanged}
                                onChange={(event, checked) => {
                                    this._handleCheckbox(CoreFieldRefNames.AssignedTo, checked);
                                }}
                            />
                            <Checkbox
                                checked={this.customOptions.has(CoreFieldRefNames.IterationPath)}
                                className="notification-options-custom-checkbox"
                                disabled={this.state.selectedSubscriptionType != FollowType.Custom}
                                label={Resources.IterationChanged}
                                onChange={(event, checked) => {
                                    this._handleCheckbox(CoreFieldRefNames.IterationPath, checked);
                                }}
                            />
                        </div>
                    </div>
                </RadioButtonGroup>
            </Dialog>
        );
    }

    private _handleSelect = (selectedId: string): void => {
        if (selectedId === FollowType.Custom) {
            this.setState({
                selectedSubscriptionType: selectedId
            });
        } else if (selectedId === FollowType.All || selectedId === FollowType.None) {
            this.customOptions.clear();
            this.setState({
                selectedSubscriptionType: selectedId
            });
        }
    };

    private _handleCheckbox = (field: string, checked: boolean): void => {
        if (this.state.selectedSubscriptionType === FollowType.Custom) {
            if (checked) {
                this.customOptions.add(field);
            } else {
                this.customOptions.delete(field);
            }
            this.forceUpdate();
        }
    };

    private _handleSave = async () => {
        if (this.originalFollowType !== FollowType.None) {
            await this.followsService.unfollow(KnownArtifactTypes.WorkItem, this.workItemId);
        }

        if (this.state.selectedSubscriptionType !== FollowType.None) {
            let description: string | undefined = undefined;
            if (this.state.selectedSubscriptionType === FollowType.Custom) {
                description = JSON.stringify({ fieldFilters: [...this.customOptions] });
            }

            await this.followsService.follow(KnownArtifactTypes.WorkItem, this.workItemId, description);
        }

        this.props.onDismiss(this.state.selectedSubscriptionType !== FollowType.None);
    };

    private _isSaveDisabled(): boolean {
        if (this.state.selectedSubscriptionType === this.originalFollowType) {
            if (
                this.originalFollowType === FollowType.Custom &&
                this.customOptions.size === this.originalCustomOptions.size &&
                [...this.customOptions].every(field => this.originalCustomOptions.has(field))
            ) {
                return true;
            } else if (this.state.selectedSubscriptionType !== FollowType.Custom) {
                return true;
            }
        }

        if (this.state.selectedSubscriptionType === FollowType.Custom && !this.customOptions.size) {
            return true;
        }

        return false;
    }
}
